import { forwardRef } from 'react';
import type { CSSProperties, HTMLAttributes } from 'react';
import s from './global.styled.module.scss';

type DivProps = HTMLAttributes<HTMLDivElement>;

const cx = (...classes: Array<string | undefined>): string | undefined => {
  const joined = classes.filter(Boolean).join(' ');
  return joined.length > 0 ? joined : undefined;
};

/* --------------- SPACER --------------------------- */

const Spacer = forwardRef<HTMLDivElement, DivProps & { height: string }>(
  ({ children, className, style, height, ...rest }, ref) => {

    const spacerStyle = height
      ? ({ '--spacer-height': height + 'px', ...style } as CSSProperties)
      : style;

    return (
      <div ref={ref} className={cx(s.spacer, className)} style={spacerStyle} {...rest}>
        {children}
      </div>
    );
  }
);
Spacer.displayName = 'Spacer';

/* --------------- ROUNDED BORDER BOX --------------- */

const RoundedBorderBox = forwardRef<HTMLDivElement, DivProps>(
  ({ children, className, ...rest }, ref) => (
    <div ref={ref} className={cx(s.roundedBorderBox, className)} {...rest}>
      {children}
    </div>
  )
);
RoundedBorderBox.displayName = 'RoundedBorderBox';

/* --------------- SPIN ----------------------------- */

const SpinWrapper = forwardRef<HTMLDivElement, DivProps>(
  ({ children, className, ...rest }, ref) => (
    <div ref={ref} className={cx(s.spinWrapper, className)} {...rest}>
      {children}
    </div>
  )
);
SpinWrapper.displayName = 'SpinWrapper';

const DefaultSpinner = forwardRef<HTMLDivElement, DivProps>(
  ({ children, className, ...rest }, ref) => (
    <div ref={ref} className={cx(s.defaultSpinner, className)} {...rest}>
      {children}
    </div>
  )
);
DefaultSpinner.displayName = 'DefaultSpinner';

/* --------------- DEFAULT HEIGHT ------------------- */

const LoadingDefaultHeight = forwardRef<HTMLDivElement, DivProps & { $minHeight: string }>(
  ({ children, className, style, $minHeight, ...rest }, ref) => {

    const heightStyle = $minHeight
      ? ({ '--default-min-height': $minHeight, ...style } as CSSProperties)
      : style;

    return (
      <div ref={ref} className={cx(s.loadingDefaultHeight, className)} style={heightStyle} {...rest}>
        {children}
      </div>
    );
  }
);
LoadingDefaultHeight.displayName = 'LoadingDefaultHeight';

/* --------------- GLOBAL CONTENT --------------------------- */

const ContentWrapper = forwardRef<HTMLDivElement, DivProps>(
  ({ children, className, ...rest }, ref) => (
    <div ref={ref} className={cx(s.contentWrapper, className)} {...rest}>
      {children}
    </div>
  )
);
ContentWrapper.displayName = 'ContentWrapper';

const ContentBody = forwardRef<HTMLDivElement, DivProps>(
  ({ children, className, ...rest }, ref) => (
    <div ref={ref} className={cx(s.contentBody, className)} {...rest}>
      {children}
    </div>
  )
);
ContentBody.displayName = 'ContentBody';

/* --------------- MODAL --------------------------- */

const ModalWrapper = forwardRef<HTMLDivElement, DivProps>(
  ({ children, className, ...rest }, ref) => (
    <div ref={ref} className={cx(s.modalWrapper, className)} {...rest}>
      {children}
    </div>
  )
);
ModalWrapper.displayName = 'ModalWrapper';

const ModalTitle = forwardRef<HTMLDivElement, DivProps & { $gap?: string }>(
  ({ children, className, style, $gap, ...rest }, ref) => {

    const gapStyle = $gap
      ? ({ '--modal-title-gap': $gap, ...style } as CSSProperties)
      : style;

    return (
      <div ref={ref} className={cx(s.modalTitle, className)} style={gapStyle} {...rest}>
        {children}
      </div>
    );
  }
);
ModalTitle.displayName = 'ModalTitle';

const ModalContent = forwardRef<HTMLDivElement, DivProps>(
  ({ children, className, ...rest }, ref) => (
    <div ref={ref} className={cx(s.modalContent, className)} {...rest}>
      {children}
    </div>
  )
);
ModalContent.displayName = 'ModalContent';

export {
  SpinWrapper,
  DefaultSpinner,
  Spacer,
  RoundedBorderBox,
  LoadingDefaultHeight,
  ContentWrapper,
  ContentBody,
  ModalWrapper,
  ModalTitle,
  ModalContent
};
